import { fetchAuth } from '../../../shared/api/ApiService'

export interface IDocumentType {
    value: string;
    label: string;
}

export interface IDocumentItem {
    id: string;
    type: string;
    file_name: string;
    content_type: string;
    upload_at: string;
    created_at: string;
    user_id?: number;
    user_name?: string;
}

export interface IDocumentsFilter {
    from: string;
    to: string;
    types: string[];
}

export interface IDownloadedDocument {
    content: string;
    file_name: string;
    content_type: string;
}

const apiUrl = (path: string) => `${process.env.REACT_APP_SERVER_URL}${path}`

const typeLabel = (value: string) => ({
    USER_DOCUMENT_TYPE_GPH: 'ГПХ договор',
    USER_DOCUMENT_TYPE_TERMINATION: 'Договор о расторжении',
    USER_DOCUMENT_TYPE_ACT: 'Акт',
    USER_DOCUMENT_TYPE_TASK: 'Задание',
    USER_DOCUMENT_TYPE_RECEIPT: 'Чек',
    USER_DOCUMENT_TYPE_EMPLOYMENT_CONTRACT: 'Трудовой договор',
    EMPLOYMENT_CONTRACT: 'Трудовой договор',
    'EMPLOYMENT CONTRACT': 'Трудовой договор',
    employment_contract: 'Трудовой договор',
    STUDENT_DOCUMENT_TYPE_UNSPECIFIED: 'Неизвестный тип',
    STUDENT_DOCUMENT_TYPE_ACT: 'Акт',
    STUDENT_DOCUMENT_TYPE_TASK: 'Задание',
}[value] || value
    .replace(/^USER_DOCUMENT_TYPE_/, '')
    .replace(/^STUDENT_DOCUMENT_TYPE_/, '')
    .replace(/_/g, ' '))

const mapType = (type: unknown): IDocumentType | null => {
    if(typeof type === 'string') return {value: type, label: typeLabel(type)}
    if(!type || typeof type !== 'object') return null
    const data = type as Record<string, unknown>
    const value = String(data.value ?? data.type ?? data.code ?? '')
    if(!value) return null
    const label = String(data.label ?? data.name ?? typeLabel(value))
    return {value, label: typeLabel(label)}
}

const mapDocument = (item: unknown): IDocumentItem => {
    const data = item as Record<string, unknown>
    const userId = Number(data.userId ?? data.user_id)
    return {
        id: String(data.id ?? data.documentId ?? data.document_id ?? ''),
        type: String(data.type ?? ''),
        file_name: String(data.fileName ?? data.file_name ?? ''),
        content_type: String(data.contentType ?? data.content_type ?? 'application/octet-stream'),
        upload_at: String(data.uploadAt ?? data.upload_at ?? ''),
        created_at: String(data.createdAt ?? data.created_at ?? ''),
        user_id: Number.isFinite(userId) && userId > 0 ? userId : undefined,
        user_name: data.userName || data.user_name || data.fullName || data.full_name
            ? String(data.userName ?? data.user_name ?? data.fullName ?? data.full_name)
            : undefined,
    }
}

const parseTypes = (data: unknown): IDocumentType[] => {
    const root = data as Record<string, unknown>
    const values = Array.isArray(data) ? data : (root.types ?? root.documentTypes ?? root.document_types ?? [])
    return Array.isArray(values) ? values.map(mapType).filter((item): item is IDocumentType => Boolean(item)) : []
}

const parseDocuments = (data: unknown): IDocumentItem[] => {
    const root = data as Record<string, unknown>
    const values = Array.isArray(data) ? data : (root.documents ?? root.items ?? [])
    return Array.isArray(values) ? values.map(mapDocument) : []
}

const parseDownloadedDocument = (data: unknown): IDownloadedDocument => {
    const root = data as Record<string, unknown>
    const item = (root.document && typeof root.document === 'object' ? root.document : root) as Record<string, unknown>
    return {
        content: String(item.content ?? ''),
        file_name: String(item.fileName ?? item.file_name ?? 'document'),
        content_type: String(item.contentType ?? item.content_type ?? 'application/octet-stream'),
    }
}

const send = async (path: string, method: string, body?: unknown): Promise<unknown> => {
    const response = await fetchAuth(apiUrl(path), {
        method,
        body: body === undefined ? undefined : JSON.stringify(body),
    })
    const text = await response.text()
    return text ? JSON.parse(text) : {}
}

class DocumentsService {
    async getUserTypes(userId: number) {
        return parseTypes(await send(`/api/v1/users/${userId}/documents/types`, 'GET'))
    }

    async getStudentTypes(studentId: number) {
        return parseTypes(await send(`/api/v1/students/${studentId}/documents/types`, 'GET'))
    }

    async listUser(userId: number, filter?: IDocumentsFilter) {
        return parseDocuments(await send(`/api/v1/users/${userId}/documents/list`, 'POST', filter))
    }

    async listStudent(studentId: number, filter?: IDocumentsFilter) {
        return parseDocuments(await send(`/api/v1/students/${studentId}/documents/list`, 'POST', filter))
    }

    async listUsers(filter?: IDocumentsFilter) {
        return parseDocuments(await send('/api/v1/users/documents/list', 'POST', filter))
    }

    async uploadUser(userId: number, data: Omit<IDocumentItem, 'id' | 'created_at'> & {content: string}) {
        await send(`/api/v1/users/${userId}/documents`, 'POST', data)
    }

    async uploadStudent(studentId: number, data: Omit<IDocumentItem, 'id' | 'created_at'> & {content: string}) {
        await send(`/api/v1/students/${studentId}/documents`, 'POST', data)
    }

    async downloadUser(userId: number, documentId: string) {
        return parseDownloadedDocument(await send(`/api/v1/users/${userId}/documents/${documentId}/download`, 'GET'))
    }

    async downloadStudent(studentId: number, documentId: string) {
        return parseDownloadedDocument(await send(`/api/v1/students/${studentId}/documents/${documentId}/download`, 'GET'))
    }

    async downloadUsersZip(filter?: IDocumentsFilter) {
        return parseDownloadedDocument(await send('/api/v1/users/documents/download', 'POST', filter))
    }

    async downloadUserZip(userId: number, filter?: IDocumentsFilter) {
        return parseDownloadedDocument(await send(`/api/v1/users/${userId}/documents/download`, 'POST', filter))
    }

    async downloadStudentZip(studentId: number, filter?: IDocumentsFilter) {
        return parseDownloadedDocument(await send(`/api/v1/students/${studentId}/documents/download`, 'POST', filter))
    }

    async deleteUser(userId: number, documentId: string) {
        await send(`/api/v1/users/${userId}/documents/${documentId}`, 'DELETE')
    }

    async deleteStudent(studentId: number, documentId: string) {
        await send(`/api/v1/students/${studentId}/documents/${documentId}`, 'DELETE')
    }

    async archiveUser(userId: number, data: {
        comment: string;
        termination_file_name?: string;
        termination_content_type?: string;
        termination_content?: string;
        termination_upload_at?: string;
    }) {
        await send(`/api/v1/users/${userId}/archive`, 'POST', data)
    }

    async unarchiveUser(userId: number) {
        await send(`/api/v1/users/${userId}/unarchive`, 'POST', {})
    }
}

export const documentsService = new DocumentsService()

export const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Не удалось прочитать файл'))
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '')
    reader.readAsDataURL(file)
})

export const downloadBase64 = ({content, file_name, content_type}: IDownloadedDocument) => {
    const normalized = content.replace(/^data:[^;]+;base64,/, '').replace(/-/g, '+').replace(/_/g, '/')
    const binary = window.atob(normalized)
    const bytes = new Uint8Array(binary.length)
    for(let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
    const url = URL.createObjectURL(new Blob([bytes], {type: content_type}))
    const link = document.createElement('a')
    link.href = url
    link.download = file_name
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
}
